





























import axios from 'axios';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import ApiCancelError from '@/resources/error/ApiCancelError';
import ApiModalError from '@/resources/error/ApiModalError';
import { ErrorCode } from '@/resources/error/ErrorCode';
import { ErrorType } from '@/resources/error/ErrorType';
import { CameraChildErrorCode } from '@/resources/error/CameraChildErrorCode';
import CameraPreview from '@/components/CameraPreview.vue';
import { BComponent } from 'buefy/types/components';
import { OsType, getOSType } from '@/util/device';

@Component({
  components: {
    CameraPreview
  }
})
export default class Page extends Vue {
  private isInitialized: boolean = false;
  private isCameraMount: boolean = true;
  private cameraMountTimerId: number = -1;
  private handleResizeTimerId: number = -1;
  private detectTimerId: number = -1;
  private buefyReference: BComponent | null = null;
  private isAlreadyPermitted: boolean = false;

  $refs!: {
    preview: CameraPreview;
  };

  @Watch('facingMode')
  handleFacingMode(value: VideoFacingModeEnum | 'none') {
    this.isInitialized = false;

    // カメラ切り替え時にCameraPreviewを一度アンマウントすることにより初期化する
    this.isCameraMount = false;
    window.clearTimeout(this.cameraMountTimerId);
    this.cameraMountTimerId = window.setTimeout((): void => {
      this.isCameraMount = true;
    }, 100);
  }

  @Watch('isNotFound')
  handleNotFound(value: boolean) {
    if (value) {
      const code: ErrorCode = ErrorCode.Unknown;
      this.$router.replace({ name: 'application-error', params: { code } });
    }
  }

  // 自動撮影トリガー
  @Watch('triggerDetect')
  private handleDetect(value: number): void {
    this.onDetect();
  }

  // 手動撮影トリガー
  @Watch('triggerShoot')
  private handleShoot(value: boolean): void {
    if (!this.$refs.preview) return;
    if (value) {
      this.$refs.preview.pauseVideo();
      this.onShoot();
    } else {
      this.$refs.preview.replayVideo();
    }
  }

  private get isNotFound(): boolean {
    return this.$store.state.camera.isNotFound;
  }

  private get triggerDetect(): number {
    return this.$store.state.camera.triggerDetect;
  }

  private get triggerShoot(): boolean {
    return this.$store.state.camera.triggerShoot;
  }

  private get isPermittedCameraAccess() {
    return this.$store.getters['camera/isPermittedCameraAccess'];
  }

  private get isGpDocPage(): boolean {
    return this.$store.getters['camera/isGpdoc'];
  }

  private get isOpDocPage(): boolean {
    return this.$store.getters['camera/isOpdoc'];
  }

  private get isGeneralBook(): boolean {
    return this.$store.getters['camera/isGeneralBook'];
  }

  private get facingMode(): VideoFacingModeEnum | 'none' {
    return this.$store.getters['camera/facingMode'];
  }

  private get isCameraUse(): boolean {
    if (this.facingMode === 'none') return false;
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && this.isGpDocPage) return false;
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && this.isOpDocPage) return false;
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name) return true;
    if (this.$route.name === this.$store.getters['camera/locationShoot'].name) return true;
    if (this.$route.name === this.$store.getters['camera/locationError'].name) return true;
    return false;
  }

  private get isPC(): boolean {
    return this.$store.getters['camera/isPC'];
  }

  private get backgroundColor(): string {
    if (this.$route.name === this.$store.getters['camera/locationShoot'].name) return 'transparent';
    if (this.$route.name === this.$store.getters['camera/locationConfirmation'].name) return 'white';
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && this.isGpDocPage) return 'white'; // eslint-disable-line prettier/prettier
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && this.isOpDocPage) return 'white'; // eslint-disable-line prettier/prettier
    if (this.$route.name === this.$store.getters['camera/locationIntroduction'].name && !this.isPC && this.isGeneralBook) return 'white'; // eslint-disable-line prettier/prettier
    return this.$store.state.camera.isDetectStart ? 'rgba(0, 0, 0 , 0.6)' : 'rgba(0, 0, 0 , 0.7)';
  }

  async mounted(): Promise<void> {
    this.$store.commit('camera/clearSetup');
    window.addEventListener('resize', this.handleResizeScheduler);
    this.handleResize();
    await this.beforeTryToCameraAccess();
  }

  beforeDestroy(): void {
    this.$store.commit('camera/isResize', false);
    window.clearTimeout(this.cameraMountTimerId);
    window.clearTimeout(this.handleResizeTimerId);
    window.clearTimeout(this.detectTimerId);
    window.removeEventListener('resize', this.handleResizeScheduler);

    // ブラウザバック
    if (this.buefyReference) {
      this.buefyReference.close();
      this.buefyReference = null;
    }
  }

  private handleResizeScheduler(): void {
    this.$store.commit('camera/isResize', true);
    window.clearTimeout(this.handleResizeTimerId);
    this.handleResizeTimerId = window.setTimeout((): void => {
      this.handleResize();
      this.$store.commit('camera/isResize', false);
    }, 1000);
  }

  private handleResize(): void {
    // 画面サイズ記録
    if (this.isPC) {
      // innerWidthだとスクロールバーの幅も含まれる様なのでclientWidthを使用
      this.$store.commit('camera/displayWidth', document.documentElement.clientWidth);
      this.$store.commit('camera/displayHeight', document.documentElement.clientHeight);
    } else {
      // 手元の端末だとinnerWidthは存在する要素の横幅につられて実際よりも大きな横幅になってしまうことがある
      // 横向きから縦向きに戻した際にinnerWidthだとおかしくなり、その場合はclientWidthを使う方が良さそうである
      // ただしclientWidthはアドレスバーの非表示を検知できず、アドレスバーが隠れると下に隙間ができる
      // そのため、横幅の絶対値はclientWidthが正しく、縦幅の比はinnerHeightが正しいものとして計算する
      const ratio: number = document.documentElement.clientWidth / window.innerWidth;
      this.$store.commit('camera/displayWidth', window.innerWidth * ratio);
      this.$store.commit('camera/displayHeight', window.innerHeight * ratio);
    }

    // 画像サイズ記録
    if (this.$refs.preview) {
      const videoWidth: number = this.$refs.preview.video.videoWidth;
      const videoHeight: number = this.$refs.preview.video.videoHeight;
      if (videoWidth > 0 && videoHeight > 0) {
        this.$store.commit('camera/pictureWidth', videoWidth);
        this.$store.commit('camera/pictureHeight', videoHeight);
      }
    }
  }

  // 前撮影用にカメラの画像を取得してbase64に変換する
  private createBase64(
    isDetect: boolean
  ): {
    detect: string;
    shoot: string;
  } {
    const isKeepSize: boolean = this.isGpDocPage || this.isOpDocPage;
    const clippingPictureWidth: number = this.$store.state.camera.clippingPictureWidth;
    const pictureWidth: number = this.$store.state.camera.pictureWidth;
    const pictureHeight: number = this.$store.state.camera.pictureHeight;
    const displayScale: number = this.$store.getters['camera/displayScale'];
    const jpegQuarity: number = 0.9;
    const video: HTMLVideoElement = this.$refs.preview.video;
    const shootCanvas: HTMLCanvasElement = document.createElement('canvas');
    const shootContext: CanvasRenderingContext2D | null = shootCanvas.getContext('2d');
    const w: number = isKeepSize ? pictureWidth / displayScale : clippingPictureWidth;
    const h: number = pictureHeight / displayScale;
    const x: number = Math.round((pictureWidth - w) / 2);
    const y: number = Math.round((pictureHeight - h) / 2);
    shootCanvas.width = w;
    shootCanvas.height = h;
    if (shootContext) shootContext.drawImage(video, x, y, w, h, 0, 0, w, h);
    const base64Shoot: string = shootCanvas.toDataURL('image/jpeg', jpegQuarity);
    this.checkBase64(base64Shoot);

    if (!isDetect) return { detect: '', shoot: base64Shoot };

    // PC版は自動撮影の画質を変えない
    if (this.isPC) return { detect: base64Shoot, shoot: base64Shoot };

    // 自動撮影の画像容量を減らすために画像のサイズを変える
    const detectCanvas: HTMLCanvasElement = document.createElement('canvas');
    const detectContext: CanvasRenderingContext2D | null = detectCanvas.getContext('2d');
    detectCanvas.width = 320;
    detectCanvas.height = Math.round(detectCanvas.width * (h / w));
    const srcw: number = shootCanvas.width;
    const srch: number = shootCanvas.height;
    const dstw: number = detectCanvas.width;
    const dsth: number = detectCanvas.height;
    if (detectContext) detectContext.drawImage(shootCanvas, 0, 0, srcw, srch, 0, 0, dstw, dsth);
    const base64Detect: string = detectCanvas.toDataURL('image/jpeg', jpegQuarity);
    this.checkBase64(base64Detect);

    return { detect: base64Detect, shoot: base64Shoot };
  }

  // base64の確認
  private checkBase64(base64: string): void {
    if (base64 !== '' && base64 !== 'data:,') return;
    // もし空画像になってしまったらエラーページへ遷移し、エラーを投げることによりログを飛ばす
    const code: ErrorCode = ErrorCode.Unknown;
    this.$router.push({ name: 'application-error', params: { code } });
    const w: number = this.$store.state.camera.clippingPictureWidth;
    const h: number = this.$store.state.camera.pictureHeight;
    throw new Error(`empty image from canvas.toDataURL (width: ${w}, height: ${h})`);
  }

  // 初期化完了
  private onInitialize(videoWidth: number, videoHeight: number): void {
    // 画像サイズ記録
    if (this.isPC) {
      this.$store.commit('camera/pictureWidth', this.$refs.preview.video.videoWidth);
      this.$store.commit('camera/pictureHeight', this.$refs.preview.video.videoHeight);
    } else {
      // 手元の端末(Xperia X Performance)だとinitializedのタイミングではなぜか横持ちの解像度が取れたので短い方を横幅としておく
      this.$store.commit('camera/pictureWidth', Math.min(videoWidth, videoHeight));
      this.$store.commit('camera/pictureHeight', Math.max(videoWidth, videoHeight));
    }

    // 初期化完了
    this.isInitialized = true;
  }

  // 撮影の権限を得られなかった時
  private onPermissionDenied(): void {
    const osType = getOSType();
    let message: string = this.$t('camera.onPermissionDenied.message') as string;
    const title1: string = this.$t('camera.onPermissionDenied.linkTitle1') as string;
    const title2: string = this.$t('camera.onPermissionDenied.linkTitle2') as string;
    const address1: string = this.$t('camera.onPermissionDenied.linkAddress1') as string;
    const address2: string = this.$t('camera.onPermissionDenied.linkAddress2') as string;
    const tag1: string = this.$t('camera.onPermissionDenied.linkTag', { title: title1, address: address1 }) as string;
    const tag2: string = this.$t('camera.onPermissionDenied.linkTag', { title: title2, address: address2 }) as string;
    if (osType === OsType.Android) {
      message = this.$t('camera.onPermissionDenied.messageAndroid', { tag1, tag2 }) as string;
    } else if (osType === OsType.iOS || osType === OsType.iPad) {
      message = this.$t('camera.onPermissionDenied.messageIos', { tag2 }) as string;
    }

    this.$buefy.dialog.alert({
      title: this.$tc('camera.onPermissionDenied.title'),
      message,
      confirmText: this.$tc('camera.onPermissionDenied.confirmText'),
      type: 'alert',
      canCancel: false,
      onConfirm: () => {
        location.reload();
      }
    });
  }

  // ちょうど良いデバイスが存在しなかった時
  private onUnsupportedDevice(): void {
    this.$buefy.dialog.alert({
      title: this.$tc('camera.onUnsupportedDevice.title'),
      message: this.$tc('camera.onUnsupportedDevice.message'),
      confirmText: this.$tc('camera.onUnsupportedDevice.confirmText'),
      type: 'alert',
      canCancel: false,
      scroll: 'keep', // PCではダイアログを閉じた後リロード時に一瞬スクロールバーが表示されてガタつくため、最初からスクロールバーを表示させる
      onConfirm: () => {
        location.reload();
      }
    });
  }

  // 自動撮影
  private async onDetect(): Promise<void> {
    if (this.$store.state.camera.isShooting) return;

    try {
      this.$store.commit('camera/isShooting', true);

      // 画像作成
      const base64: {
        detect: string;
        shoot: string;
      } = this.createBase64(true);

      // 画像確認 通信エラーは無視する
      try {
        await this.$store.dispatch('camera/storeDetectData', base64.detect);
      } catch {}

      if (this.$store.state.camera.detectCount >= 3) {
        // 画像送信
        await this.$store.dispatch('camera/storeShootData', base64.shoot);
        await this.$store.dispatch('camera/storeTrimmedPhotoData');
        this.$store.commit('camera/isDetectSuccess', true);

        // 演出待ち
        await new Promise(resolve => (this.detectTimerId = window.setTimeout(resolve, 1000)));

        // ページ遷移
        this.$router.push(this.$store.getters['camera/locationConfirmation']);
      }
    } catch (error) {
      const data: ErrorType = (axios.isAxiosError(error) && error.response && error.response.data) || {};
      if (error instanceof ApiCancelError) {
        // キャンセルは握りつぶす
      } else if (error instanceof ApiModalError) {
        // clientRetryableで処理したエラーは握りつぶす
      } else if (
        data.error_code === ErrorCode.CVInvalidFeature ||
        data.error_code === ErrorCode.CVDifferentDriverLicense
      ) {
        // 撮影結果の自動判定エラー処理
        this.$store.commit('camera/addRetryCount');
        const isManyRetry: boolean = this.$store.state.camera.retryCount >= 5;
        const errorCodes: string[] = isManyRetry ? [CameraChildErrorCode.ManyRetry] : data.fields || [];
        const fields: string[] = [CameraChildErrorCode.Automatic as string].concat(errorCodes);
        this.$router.push(Object.assign({}, this.$store.getters['camera/locationError'], { query: { fields } }));
      } else throw error;
    } finally {
      this.$store.commit('camera/isShooting', false);
    }
  }

  // 手動撮影
  private async onShoot(): Promise<void> {
    if (this.$store.state.camera.isShooting) return;

    try {
      this.$store.commit('camera/isShooting', true);

      // 画像作成
      const base64: {
        detect: string;
        shoot: string;
      } = this.createBase64(false);

      // 画像送信
      await this.$store.dispatch('camera/storeShootData', base64.shoot);
      await this.$store.dispatch('camera/storeTrimmedPhotoData');

      // ページ遷移
      this.$router.push(this.$store.getters['camera/locationConfirmation']);
    } catch (error) {
      const data: ErrorType = (axios.isAxiosError(error) && error.response && error.response.data) || {};
      if (error instanceof ApiCancelError) {
        // キャンセルは握りつぶす
      } else if (error instanceof ApiModalError) {
        // clientRetryableで処理したエラーは握りつぶす
      } else if (
        data.error_code === ErrorCode.CVInvalidFeature ||
        data.error_code === ErrorCode.CVDifferentDriverLicense
      ) {
        // 撮影結果の自動判定エラー処理
        this.$store.commit('camera/addRetryCount');
        const isManyRetry: boolean = this.$store.state.camera.retryCount >= 5;
        const errorCodes: string[] = isManyRetry ? [CameraChildErrorCode.ManyRetry] : data.fields || [];
        const fields: string[] = [CameraChildErrorCode.Automatic as string].concat(errorCodes);
        this.$router.push(Object.assign({}, this.$store.getters['camera/locationError'], { query: { fields } }));
      } else throw error;
    } finally {
      this.$store.commit('camera/isShooting', false);
    }
  }

  private async checkBrowserCameraPermission(): Promise<boolean> {
    // カメラを許可しているかどうかを確認
    if (navigator.permissions) {
      try {
        const result: PermissionStatus = await navigator.permissions.query({ name: 'camera' } as any); // EKYCDEV-2547 クライアントバージョンアップによりエラーが出るようになったので、動作を変えないようにエラーが出ないようにする。 TODO: チケットを作ってちゃんと修正
        const state: PermissionState = result.state;
        return state === 'granted';
      } catch (error) {}
    }
    return false;
  }

  private async beforeTryToCameraAccess(): Promise<void> {
    // 許可を出している(Vuex)
    if (this.isPermittedCameraAccess) return;

    // 許可を出している(ブラウザ側)
    this.isAlreadyPermitted = await this.checkBrowserCameraPermission();

    if (this.isAlreadyPermitted) {
      this.$store.commit('camera/isPermittedCameraAccess', true);
    } else {
      this.buefyReference = this.$buefy.dialog.alert({
        title: this.$tc('camera.onAnnounce.title'),
        message: this.$tc('camera.onAnnounce.message'),
        confirmText: this.$tc('camera.onAnnounce.confirmText'),
        type: 'alert',
        canCancel: false,
        onConfirm: () => {
          this.$store.commit('camera/isPermittedCameraAccess', true);
        }
      });
    }
  }
}
